import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, PackagePlus, Trash2 } from 'lucide-react';
import { Section } from '@/shared/ui/layout/Section';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiParcelsProductsDelete } from '@/entities/parcels-products';
import { formatUsd } from '@/shared/utils/currency';
import { FeaturesParcelProductsFormDialog } from '@/features/parcel-products/modules/ui/ProductFormDialog';
import type { ApiParcelsProductsGetItem } from '@/shared/types/api/parcels-products';

type Props = {
  parcelId: string;
  products: ApiParcelsProductsGetItem[];
};

export const FeaturesParcelProducts = ({ parcelId, products }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ApiParcelsProductsGetItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiParcelsProductsGetItem | null>(null);

  const remove = useMutate({
    api: (productId: string) => apiParcelsProductsDelete(parcelId, productId),
    invalidateKeys: [queryKeys.parcels.all, queryKeys.parcels.products(parcelId), queryKeys.pinfl.all],
    onSuccess: () => {
      toast.info(t('parcels.products.toast.deleted'));
      setDeleteTarget(null);
    },
    onError: (_e, message) => toast.error(message),
  });

  const total = products.reduce((s, p) => s + p.quantity * p.price_per_item, 0);

  return (
    <Section
      title={t('parcels.products.title', { count: products.length })}
      action={
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<PackagePlus className="h-4 w-4" />}
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          {t('parcels.products.add')}
        </Button>
      }
    >
      {products.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('parcels.products.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-2">{t('parcels.products.columns.name')}</th>
                <th className="px-3 py-2 text-right">{t('parcels.products.columns.quantity')}</th>
                <th className="px-3 py-2 text-right">{t('parcels.products.columns.unitPrice')}</th>
                <th className="px-3 py-2 text-right">{t('parcels.products.columns.total')}</th>
                <th className="px-3 py-2 text-right">{t('parcels.products.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-ink-soft">{p.full_name}</td>
                  <td className="px-3 py-2 text-right">{p.quantity}</td>
                  <td className="px-3 py-2 text-right">{formatUsd(p.price_per_item)}</td>
                  <td className="px-3 py-2 text-right font-medium">
                    {formatUsd(p.quantity * p.price_per_item)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      <IconButton
                        label={t('parcels.products.edit')}
                        onClick={() => {
                          setEditing(p);
                          setFormOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </IconButton>
                      <IconButton
                        label={t('parcels.products.delete')}
                        variant="danger"
                        onClick={() => setDeleteTarget(p)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-sm">
        <span className="text-ink-muted">{t('parcels.products.grandTotal')}</span>
        <span className="text-base font-semibold text-ink">{formatUsd(total)}</span>
      </div>

      <FeaturesParcelProductsFormDialog
        open={formOpen}
        parcelId={parcelId}
        editing={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={t('parcels.products.confirmDelete.title')}
        description={t('parcels.products.confirmDelete.description', {
          name: deleteTarget?.full_name ?? '',
        })}
        confirmLabel={t('parcels.products.confirmDelete.confirm')}
        cancelLabel={t('parcels.products.confirmDelete.cancel')}
        confirmVariant="danger"
        loading={remove.isPending}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </Section>
  );
};
